import { useMemo } from 'react';

import { EmployeeStatusBadge } from '@/common/components/shared/badge/EmployeeStatusBadge';
import { NoResult } from '@/common/components/shared/no-result/NoResult';
import { Table, type TableLayout } from '@/common/components/shared/table/Table';
import type { ListEmployeesForSearchPopupQuery } from '@/common/graphql';
import { JobHistoryLevelLabels, type TableSelection } from '@/common/types';
import { DATETIME_FORMATS, DateUtils } from '@/common/utils';

export type EmployeeSearchResultRow = ListEmployeesForSearchPopupQuery['listEmployees']['items'][number];

interface EmployeeSearchTableProps {
  loading: boolean;
  items: EmployeeSearchResultRow[];
  page?: number;
  total?: number;
  onRowClick?: (row: EmployeeSearchResultRow) => void;
  selection?: TableSelection<EmployeeSearchResultRow>;
  onSelectionChange?: (selection: TableSelection<EmployeeSearchResultRow>) => void;
  selectedIds?: string[];
}

export const EmployeeSearchTable = ({
  items,
  loading,
  page = 1,
  total = 0,
  onRowClick,
  selection,
  onSelectionChange,
  selectedIds = [],
}: EmployeeSearchTableProps) => {
  const layout = useMemo(
    () =>
      [
        {
          headerRender: () => 'Name',
          accessor: 'id',
          minWidth: '15rem',
          cellRender: (row: EmployeeSearchResultRow) =>
            selection ? (
              row.generalInformation.preferredFullName
            ) : (
              <div className="text-blue-6 cursor-pointer" onClick={() => onRowClick?.(row)}>
                {row.generalInformation.preferredFullName}
              </div>
            ),
        },
        {
          headerRender: () => 'Status',
          accessor: 'status',
          thClassName: 'text-center',
          tdClassName: 'text-center',
          cellRender: ({ status }) => <EmployeeStatusBadge status={status} />,
        },
        {
          headerRender: () => 'Company',
          accessor: 'company',
          cellRender: ({ currentMainContract }) =>
            currentMainContract && (
              <>
                <div>{currentMainContract.company.name}</div>
                <div className="text-gray-7 text-2xs">{currentMainContract.company.country.name}</div>
              </>
            ),
        },
        {
          headerRender: () => 'Level',
          accessor: 'level',
          thClassName: 'text-center',
          tdClassName: 'text-center tabular-nums',
          cellRender: ({ currentPosition }) =>
            currentPosition && <span>{JobHistoryLevelLabels[currentPosition.level]}</span>,
        },
        {
          headerRender: () => 'Business Unit',
          accessor: 'businessUnit',
          truncated: true,
          cellRender: ({ currentPosition }) => currentPosition && <span>{currentPosition.businessUnit.name}</span>,
        },
        {
          headerRender: () => 'Department',
          accessor: 'department',
          truncated: true,
          cellRender: ({ currentPosition }) => currentPosition && <span>{currentPosition.department.name}</span>,
        },
        {
          headerRender: () => 'Job',
          accessor: 'job',
          truncated: true,
          cellRender: ({ currentPosition }) => currentPosition && <span>{currentPosition.job.name}</span>,
        },
        {
          headerRender: () => 'Job Title',
          accessor: 'jobTitle',
          width: 'auto',
          cellRender: ({ currentPosition }) => currentPosition && <span>{currentPosition.jobTitle}</span>,
        },
        {
          headerRender: () => 'Hired date',
          accessor: 'hiredDate',
          width: 'auto',
          tdClassName: 'text-right tabular-nums',
          cellRender: ({ generalInformation: { hiredDate } }) =>
            DateUtils.formatDate(hiredDate, DATETIME_FORMATS.MMMddyyyy),
        },
      ] as TableLayout<EmployeeSearchResultRow>[],
    [onRowClick, selection],
  );

  return (
    <Table<EmployeeSearchResultRow>
      allowFloatingHeader={true}
      data={items}
      hoverable={true}
      layout={layout}
      loading={loading}
      notFoundTemplate={<NoResult description="Try changing the filters or search terms" title="No employees found" />}
      // Caps the scrollable table body to the viewport height minus the modal's own chrome that
      // surrounds it (title bar, EmployeeSearchFilters fieldset, TablePagination bar, ModalFooter,
      // and modal padding) so the table scrolls internally instead of growing the whole modal past
      // the viewport. These are hand-tuned against the rendered layout, not derived from any single
      // component's fixed height - re-measure against the actual modal if this layout changes.
      scrollContainerClassName="min-h-[200px] max-h-[calc(100dvh_-_128px_-_64px_-_160px_-_48px_-_64px)]"
      trClassName={(row: EmployeeSearchResultRow) => (selectedIds.includes(row.id) ? 'bg-blue-0' : '')}
      page={page}
      selectable={selection && onSelectionChange && { byField: 'id', total }}
      selection={selection}
      onSelectionChange={onSelectionChange}
      onClick={onRowClick}
    />
  );
};
